import React, { useState, useEffect } from 'react';
import { Link2, ExternalLink, CheckCircle2, XCircle, AlertTriangle, Lock, Copy, Check, RefreshCw, ListTodo, ArrowRight, Shield, FileText, Globe, Zap, Star, Clock, Code } from 'lucide-react';
import { t } from '../services/i18n';
import { authService } from '../services/authService';

interface BacklinkBuilderProps {
  currentUser: any;
}

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  category: 'backlinks' | 'redirects';
}

export const BacklinkBuilder: React.FC<BacklinkBuilderProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState<'backlinks' | 'redirects'>('backlinks');
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Check if user has monthly plan or higher
  const isPremium = currentUser && 
    currentUser.plan !== 'free' && 
    currentUser.subscriptionExpiry && 
    new Date(currentUser.subscriptionExpiry) > new Date();

  // Load todos from localStorage
  useEffect(() => {
    const savedTodos = localStorage.getItem('orca_backlink_todos');
    if (savedTodos) {
      try {
        setTodos(JSON.parse(savedTodos));
      } catch (e) {
        console.error('Failed to load todos:', e);
      }
    }
  }, []);

  // Save todos to localStorage
  useEffect(() => {
    if (todos.length > 0) {
      localStorage.setItem('orca_backlink_todos', JSON.stringify(todos));
    }
  }, [todos]);

  const toggleTodo = (id: string) => {
    if (!isPremium) {
      setShowUpgrade(true);
      return;
    }
    setTodos(todos.map(todo => 
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const addTodo = (text: string, category: 'backlinks' | 'redirects') => {
    if (!isPremium) {
      setShowUpgrade(true);
      return;
    }
    const newTodo: TodoItem = {
      id: Date.now().toString(),
      text,
      completed: false,
      category
    };
    setTodos([...todos, newTodo]);
  };

  const resetTodos = () => {
    if (!isPremium) {
      setShowUpgrade(true);
      return;
    }
    const confirmMessage = t('backlinks.resetConfirm') || 'Are you sure you want to reset all todos?';
    if (confirm(confirmMessage)) {
      setTodos([]);
      localStorage.removeItem('orca_backlink_todos');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    if (!isPremium) {
      setShowUpgrade(true);
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Prevent text selection and right-click
  useEffect(() => {
    if (!isPremium) {
      const handleContextMenu = (e: MouseEvent) => e.preventDefault();
      const handleSelectStart = (e: Event) => e.preventDefault();
      const handleCopy = (e: ClipboardEvent) => e.preventDefault();
      const handleDragStart = (e: DragEvent) => e.preventDefault();

      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('selectstart', handleSelectStart);
      document.addEventListener('copy', handleCopy);
      document.addEventListener('dragstart', handleDragStart);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('selectstart', handleSelectStart);
        document.removeEventListener('copy', handleCopy);
        document.removeEventListener('dragstart', handleDragStart);
      };
    }
  }, [isPremium]);

  const handleUpgrade = () => {
    // Trigger payment modal in parent App component via custom event
    const event = new CustomEvent('showPaywall');
    window.dispatchEvent(event);
    setShowUpgrade(false);
  };

  return (
    <div className="p-8 animate-fadeIn text-white min-h-screen" style={{ userSelect: isPremium ? 'auto' : 'none' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <Link2 className="text-indigo-400" size={30} />
              {t('backlinks.title') || 'Backlink Builder & 301 Redirect Guide'}
            </h2>
            <p className="text-slate-400 mt-2">
              {t('backlinks.subtitle') || 'Free backlink sources and 301 redirect strategies to boost your SEO'}
            </p>
          </div>
          {!isPremium && (
            <div className="flex items-center gap-3">
              <Lock className="text-yellow-400" size={20} />
              <span className="text-yellow-400 font-semibold">
              {t('backlinks.premiumRequired') || 'Monthly Plan Required'}
            </span>
            </div>
          )}
        </div>

        {/* Premium Gate Modal */}
        {showUpgrade && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 border border-indigo-500/30 rounded-2xl p-8 max-w-md w-full">
              <div className="text-center mb-6">
                <Lock className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t('backlinks.upgradeTitle') || 'Upgrade Required'}</h3>
                <p className="text-slate-300">
                  {t('backlinks.upgradeMessage') || 'This feature requires a monthly plan or higher. Upgrade to access the full backlink builder, 301 redirect guide, and todo tracking system.'}
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleUpgrade}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-6 py-3 rounded-xl font-bold transition-all"
                >
                  {t('backlinks.viewPlans') || 'View Plans & Upgrade'}
                </button>
                <button
                  onClick={() => setShowUpgrade(false)}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl font-medium transition-all"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('backlinks')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'backlinks'
                ? 'border-indigo-400 text-indigo-400'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            {t('backlinks.freeBacklinks') || 'Free Backlink Sources'}
          </button>
          {isPremium ? (
            <button
              onClick={() => setActiveTab('redirects')}
              className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
                activeTab === 'redirects'
                  ? 'border-indigo-400 text-indigo-400'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              {t('backlinks.redirectGuide') || '301 Redirect Guide'}
            </button>
          ) : (
            <button
              onClick={() => setShowUpgrade(true)}
              className="px-6 py-3 font-semibold border-b-2 border-transparent text-slate-500 hover:text-slate-400 transition-colors flex items-center gap-2"
            >
              <Lock size={16} />
              {t('backlinks.redirectGuide') || '301 Redirect Guide'}
            </button>
          )}
        </div>

        {/* Todo Sidebar */}
        {isPremium && (
          <div className="fixed right-8 top-24 w-80 bg-slate-800 border border-slate-700 rounded-2xl p-6 max-h-[calc(100vh-8rem)] overflow-y-auto z-40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <ListTodo size={20} />
                {t('backlinks.todoList') || 'Action Checklist'}
              </h3>
              <button
                onClick={resetTodos}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                title={t('backlinks.resetTodos') || 'Reset all todos'}
              >
                <RefreshCw size={14} />
                {t('backlinks.reset') || 'Reset'}
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {todos.filter(t => t.category === activeTab).length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">
                  {t('backlinks.noTodos') || 'No tasks yet. Click on items below to add them to your checklist.'}
                </p>
              ) : (
                todos
                  .filter(t => t.category === activeTab)
                  .map(todo => (
                    <div
                      key={todo.id}
                      className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors cursor-pointer"
                      onClick={() => toggleTodo(todo.id)}
                    >
                      {todo.completed ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-slate-500 rounded-full flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm flex-1 ${todo.completed ? 'line-through text-slate-500' : 'text-slate-300'}`}>
                        {todo.text}
                      </span>
                    </div>
                  ))
              )}
            </div>
            <div className="text-xs text-slate-500 text-center">
              {todos.filter(t => t.category === activeTab && t.completed).length} / {todos.filter(t => t.category === activeTab).length} {t('backlinks.completed') || 'completed'}
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`${isPremium ? 'mr-96' : ''} transition-all duration-300`}>
          {activeTab === 'backlinks' ? (
            <FreeBacklinksSection 
              isPremium={isPremium}
              onAddTodo={addTodo}
              onCopy={copyToClipboard}
              copiedId={copiedId}
              currentUser={currentUser}
              onShowUpgrade={() => setShowUpgrade(true)}
            />
          ) : isPremium ? (
            <RedirectGuideSection 
              isPremium={isPremium}
              onAddTodo={addTodo}
              onCopy={copyToClipboard}
              copiedId={copiedId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Free Backlinks Section Component
const FreeBacklinksSection: React.FC<{
  isPremium: boolean;
  onAddTodo: (text: string, category: 'backlinks' | 'redirects') => void;
  onCopy: (text: string, id: string) => void;
  copiedId: string | null;
  currentUser: any;
  onShowUpgrade: () => void;
}> = ({ isPremium, onAddTodo, onCopy, copiedId, currentUser, onShowUpgrade }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['top-tier']));

  const toggleSection = (id: string) => {
    const newSet = new Set(expandedSections);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedSections(newSet);
  };

  const backlinkPlatforms = [
    {
      id: 'top-tier',
      title: t('backlinks.topTier') || '⭐ TOP TIER - BEST FOR ARTICLE PUBLISHING',
      platforms: [
        { name: 'Medium.com', da: '95', desc: t('backlinks.mediumDesc') || 'Publish unlimited articles, add multiple links per article', url: 'https://medium.com', links: t('backlinks.unlimited') || 'Yes, unlimited in articles', approval: t('backlinks.instant') || 'Instant publishing', traffic: t('backlinks.high') || 'HIGH - millions of readers', bestFor: t('backlinks.anyNiche') || 'Any niche - business, tech, lifestyle, etc.' },
        { name: 'LinkedIn (Publish Articles)', da: '98', desc: t('backlinks.linkedinDesc') || 'Publish long-form articles on LinkedIn Pulse', url: 'https://linkedin.com', links: t('backlinks.multiple') || 'Yes, multiple links per article', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.high') || 'HIGH - professional audience', bestFor: t('backlinks.business') || 'Business, marketing, professional content' },
        { name: 'Dev.to', da: '85', desc: t('backlinks.devtoDesc') || 'Tech/developer blogging platform', url: 'https://dev.to', links: t('backlinks.unlimited') || 'Yes, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumHigh') || 'MEDIUM-HIGH for tech content', bestFor: t('backlinks.tech') || 'Programming, web development, tech tutorials' },
        { name: 'Hashnode', da: '75', desc: t('backlinks.hashnodeDesc') || 'Developer blogging with custom domain option', url: 'https://hashnode.com', links: t('backlinks.unlimited') || 'Yes, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM for tech', bestFor: t('backlinks.developers') || 'Developers, tech enthusiasts' },
        { name: 'HackerNoon', da: '80', desc: t('backlinks.hackernoonDesc') || 'Tech journalism platform', url: 'https://hackernoon.com', links: t('backlinks.contextual') || 'Yes, contextual links', approval: t('backlinks.editorial24') || 'Editorial review (usually 24-48 hours)', traffic: t('backlinks.high') || 'HIGH for tech/startup content', bestFor: t('backlinks.techStartup') || 'Tech, crypto, startups, AI' },
      ]
    },
    {
      id: 'web2',
      title: t('backlinks.web2') || '📝 WEB 2.0 BLOGGING PLATFORMS (FULL CONTROL)',
      platforms: [
        { name: 'WordPress.com', da: '100', desc: t('backlinks.wordpressDesc') || 'Full blog at yourname.wordpress.com', url: 'https://wordpress.com', links: t('backlinks.yesUnlimited') || 'YES, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.lowGrows') || 'LOW initially, grows over time', bestFor: t('backlinks.anyNiche') || 'Any niche, long-term blogging' },
        { name: 'Blogger.com', da: '100', desc: t('backlinks.bloggerDesc') || 'Google-owned blog platform', url: 'https://blogger.com', links: t('backlinks.yesUnlimited') || 'YES, unlimited', approval: t('backlinks.instantGoogle') || 'Instant (Google account needed)', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.googleServices') || 'Any niche, especially if you use other Google services' },
        { name: 'Tumblr', da: '99', desc: t('backlinks.tumblrDesc') || 'Microblogging + long articles', url: 'https://tumblr.com', links: t('backlinks.yesUnlimited') || 'YES, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.creative') || 'Creative content, visual + text mix' },
        { name: 'Wix.com (FREE plan)', da: '93', desc: t('backlinks.wixDesc') || 'Free website + blog', url: 'https://wix.com', links: t('backlinks.yesInBlog') || 'YES in blog posts', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.lowInitially') || 'LOW initially', bestFor: t('backlinks.portfolio') || 'Portfolio + blog combo' },
        { name: 'Weebly (FREE plan)', da: '92', desc: t('backlinks.weeblyDesc') || 'Free website with blog', url: 'https://weebly.com', links: t('backlinks.yes') || 'YES', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.lowInitially') || 'LOW initially', bestFor: t('backlinks.simpleBlog') || 'Simple blog + business site' },
      ]
    },
    {
      id: 'community',
      title: t('backlinks.community') || '🎓 COMMUNITY & NICHE PLATFORMS',
      platforms: [
        { name: 'Substack', da: '88', desc: t('backlinks.substackDesc') || 'Newsletter platform (publish on web too)', url: 'https://substack.com', links: t('backlinks.yesUnlimited') || 'YES, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumHigh') || 'MEDIUM-HIGH', bestFor: t('backlinks.newsletters') || 'Newsletters, thought leadership' },
        { name: 'HubPages', da: '82', desc: t('backlinks.hubpagesDesc') || 'Article publishing with revenue sharing', url: 'https://hubpages.com', links: t('backlinks.twoLinks') || 'YES, 2 links per article (to your site)', approval: t('backlinks.quality24') || 'Quality review (24 hours)', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.howTo') || 'How-to guides, tutorials, reviews' },
        { name: 'EzineArticles', da: '65', desc: t('backlinks.ezineDesc') || 'Article directory', url: 'https://ezinearticles.com', links: t('backlinks.yesInBio') || 'YES, in author bio', approval: t('backlinks.editorialSlower') || 'Editorial review (slower)', traffic: t('backlinks.lowMedium') || 'LOW-MEDIUM', bestFor: t('backlinks.howToEducational') || 'How-to articles, educational content' },
        { name: 'ArticlesBase', da: '53', desc: t('backlinks.articlesbaseDesc') || 'Article submission platform', url: 'https://articlesbase.com', links: t('backlinks.yesInArticle') || 'YES, in article + author box', approval: t('backlinks.quickReview') || 'Quick review', traffic: t('backlinks.low') || 'LOW', bestFor: t('backlinks.businessMarketing') || 'Business, marketing articles' },
      ]
    },
    {
      id: 'qa',
      title: t('backlinks.qa') || '🗣️ Q&A PLATFORMS (ARTICLE-LENGTH ANSWERS)',
      platforms: [
        { name: 'Quora', da: '93', desc: t('backlinks.quoraDesc') || 'Answer questions with detailed responses', url: 'https://quora.com', links: t('backlinks.yesWhenRelevant') || 'YES, when relevant to answer', approval: t('backlinks.instantMayMod') || 'Instant (may get moderated if spammy)', traffic: t('backlinks.veryHigh') || 'VERY HIGH', bestFor: t('backlinks.anyNicheQuestions') || 'Any niche, answering specific questions' },
        { name: 'Reddit', da: '90', desc: t('backlinks.redditDesc') || 'Post in relevant subreddits', url: 'https://reddit.com', links: t('backlinks.yesReadRules') || 'YES, but read subreddit rules first', approval: t('backlinks.dependsSubreddit') || 'Depends on subreddit (some auto-approve)', traffic: t('backlinks.veryHighViral') || 'VERY HIGH if post goes viral', bestFor: t('backlinks.nicheCommunities') || 'Niche communities, discussions' },
      ]
    },
    {
      id: 'document',
      title: t('backlinks.document') || '📚 DOCUMENT PUBLISHING',
      platforms: [
        { name: 'SlideShare', da: '95', desc: t('backlinks.slideshareDesc') || 'Upload presentations with links', url: 'https://slideshare.net', links: t('backlinks.yesInPresentation') || 'YES, in presentation + description', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumHigh') || 'MEDIUM-HIGH', bestFor: t('backlinks.businessEducational') || 'Business, educational content as slides' },
        { name: 'Issuu', da: '93', desc: t('backlinks.issuuDesc') || 'Publish PDFs as digital publications', url: 'https://issuu.com', links: t('backlinks.yesInDocument') || 'YES, in document', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.magazines') || 'Magazines, ebooks, reports' },
        { name: 'Scribd', da: '93', desc: t('backlinks.scribdDesc') || 'Document publishing platform', url: 'https://scribd.com', links: t('backlinks.yesInDocuments') || 'YES, in documents', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.longForm') || 'Long-form documents, guides' },
        { name: 'Google Docs (Public)', da: '100', desc: t('backlinks.googledocsDesc') || 'Share public documents', url: 'https://docs.google.com', links: t('backlinks.yesUnlimited') || 'YES, unlimited', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.lowIndexed') || 'LOW directly, but indexed by Google', bestFor: t('backlinks.detailedGuides') || 'Detailed guides, resources' },
      ]
    },
    {
      id: 'tech',
      title: t('backlinks.tech') || '💻 TECH/DEVELOPER FOCUSED',
      platforms: [
        { name: 'GitHub', da: '100', desc: t('backlinks.githubDesc') || 'README files in repositories', url: 'https://github.com', links: t('backlinks.yesUnlimitedMarkdown') || 'YES, unlimited in markdown', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumTech') || 'MEDIUM for tech audience', bestFor: t('backlinks.openSource') || 'Open source projects, code documentation' },
        { name: 'GitLab', da: '88', desc: t('backlinks.gitlabDesc') || 'Similar to GitHub', url: 'https://gitlab.com', links: t('backlinks.yes') || 'YES', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.codeProjects') || 'Code projects, tech docs' },
        { name: 'Hackster.io', da: '73', desc: t('backlinks.hacksterDesc') || 'Hardware/IoT project platform', url: 'https://hackster.io', links: t('backlinks.yes') || 'YES', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumHardware') || 'MEDIUM for hardware projects', bestFor: t('backlinks.arduino') || 'Arduino, Raspberry Pi, maker projects' },
      ]
    },
    {
      id: 'creative',
      title: t('backlinks.creative') || '🎨 CREATIVE PLATFORMS',
      platforms: [
        { name: 'Behance', da: '93', desc: t('backlinks.behanceDesc') || 'Portfolio with project descriptions', url: 'https://behance.net', links: t('backlinks.yesInProject') || 'YES, in project descriptions', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.medium') || 'MEDIUM', bestFor: t('backlinks.designers') || 'Designers, photographers, creatives' },
        { name: 'Dribbble', da: '90', desc: t('backlinks.dribbbleDesc') || 'Design showcase', url: 'https://dribbble.com', links: t('backlinks.yesInProfile') || 'YES, in profile + shot descriptions', approval: t('backlinks.instant') || 'Instant', traffic: t('backlinks.mediumDesign') || 'MEDIUM for design', bestFor: t('backlinks.uiux') || 'UI/UX designers, illustrators' },
      ]
    },
  ];

  const bestPractices = [
    { title: t('backlinks.practice1') || 'Write 800-2000 words minimum', desc: t('backlinks.practice1Desc') || 'Longer = better rankings' },
    { title: t('backlinks.practice2') || 'Add 1-3 backlinks per article', desc: t('backlinks.practice2Desc') || "Don't spam links" },
    { title: t('backlinks.practice3') || 'Use natural anchor text', desc: t('backlinks.practice3Desc') || '"check out this guide" not "best SEO tools"' },
    { title: t('backlinks.practice4') || 'Write original content', desc: t('backlinks.practice4Desc') || "Don't copy-paste across platforms" },
    { title: t('backlinks.practice5') || 'Engage with communities', desc: t('backlinks.practice5Desc') || "Comment on others' posts" },
    { title: t('backlinks.practice6') || 'Be patient', desc: t('backlinks.practice6Desc') || 'Backlinks take 2-4 weeks to show effect' },
  ];

  const avoidPractices = [
    t('backlinks.avoid1') || "Don't spam - You'll get banned",
    t('backlinks.avoid2') || "Don't copy-paste - Each platform should have unique content",
    t('backlinks.avoid3') || "Don't over-optimize - No keyword stuffing",
    t('backlinks.avoid4') || "Don't only promote - Provide value first",
  ];

  return (
    <div className="space-y-8">
      {/* Warning Section */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-bold text-yellow-400 mb-2">
              {t('backlinks.avoidPenalization') || 'How to Avoid Google Penalization'}
            </h3>
            <ul className="space-y-2 text-slate-300 text-sm">
              {avoidPractices.map((practice, i) => (
                <li key={i} className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>{practice}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <CheckCircle2 className="text-emerald-400" size={24} />
          {t('backlinks.bestPractices') || '✅ Best Practices for Quality Backlinks'}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {bestPractices.map((practice, i) => (
            <div key={i} className="bg-slate-900/50 rounded-lg p-4">
              <div className="font-semibold text-white mb-1">{practice.title}</div>
              <div className="text-sm text-slate-400">{practice.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Platforms by Category */}
      {backlinkPlatforms.map((category) => (
        <div key={category.id} className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          <button
            onClick={() => toggleSection(category.id)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
          >
            <h3 className="text-lg font-bold">{category.title}</h3>
            <ArrowRight 
              className={`transition-transform ${expandedSections.has(category.id) ? 'rotate-90' : ''}`}
              size={20}
            />
          </button>
          {expandedSections.has(category.id) && (
            <div className="p-6 space-y-4">
              {category.platforms.map((platform, idx) => {
                const isLocked = !isPremium && idx >= 3;
                return (
                <div
                  key={idx}
                  className={`bg-slate-900/50 rounded-lg p-5 border border-slate-700 hover:border-indigo-500/50 transition-all relative ${
                    isLocked ? 'blur-sm pointer-events-none opacity-50' : ''
                  }`}
                >
                  {isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-900/80 rounded-lg">
                      <div className="text-center">
                        <Lock className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
                        <p className="text-white font-semibold">{t('backlinks.locked') || 'Premium Feature'}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-white">{platform.name}</h4>
                        <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs font-semibold rounded">
                          DA {platform.da}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mb-3">{platform.desc}</p>
                    </div>
                    {isPremium && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => onAddTodo(`Set up account on ${platform.name}`, 'backlinks')}
                          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                          title={t('backlinks.addToTodo') || 'Add to checklist'}
                        >
                          <ListTodo size={16} />
                        </button>
                        <a
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
                          title={t('backlinks.visitSite') || 'Visit site'}
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-400">{t('backlinks.linksAllowed') || 'Links allowed:'}</span>
                      <span className="text-white ml-2">{platform.links}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">{t('backlinks.approval') || 'Approval:'}</span>
                      <span className="text-white ml-2">{platform.approval}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">{t('backlinks.traffic') || 'Traffic potential:'}</span>
                      <span className="text-white ml-2">{platform.traffic}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">{t('backlinks.bestFor') || 'Best for:'}</span>
                      <span className="text-white ml-2">{platform.bestFor}</span>
                    </div>
                  </div>
                </div>
              );
              })}
              {!isPremium && category.platforms.length > 3 && (
                <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/30 rounded-xl p-6 text-center">
                  <Lock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-white mb-2">
                    {t('backlinks.unlockFullList') || 'Unlock Full List'}
                  </h4>
                  <p className="text-slate-300 mb-4">
                    {t('backlinks.unlockMessage') || 'Get access to all 27+ backlink platforms and the complete 301 redirect guide with a paid plan.'}
                  </p>
                  <button
                    onClick={onShowUpgrade}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl"
                  >
                    {t('backlinks.choosePlan') || 'Choose Plan & Unlock'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Recommended Starter Pack */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Star className="text-yellow-400" size={24} />
          {t('backlinks.recommendedStarter') || '🎯 Recommended Starter Pack (Maximum Impact)'}
        </h3>
        <p className="text-slate-300 mb-4">
          {t('backlinks.starterDesc') || 'Start with these 5 - they\'re the easiest and highest ROI:'}
        </p>
        <ol className="space-y-2 text-slate-300">
          <li className="flex items-start gap-2">
            <span className="font-bold text-indigo-400">1.</span>
            <span><strong>Medium</strong> - {t('backlinks.mediumStarter') || 'Publish 2 articles/week, cross-post from your blog'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-indigo-400">2.</span>
            <span><strong>LinkedIn Articles</strong> - {t('backlinks.linkedinStarter') || 'Publish 1 professional article/week'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-indigo-400">3.</span>
            <span><strong>Quora</strong> - {t('backlinks.quoraStarter') || 'Answer 5 questions/week in your niche with detailed responses'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-indigo-400">4.</span>
            <span><strong>Dev.to</strong> (if tech) - {t('backlinks.devtoStarter') || 'Publish tutorials/guides'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-indigo-400">5.</span>
            <span><strong>WordPress.com or Blogger</strong> - {t('backlinks.blogStarter') || 'Build your own blog subdomain'}</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

// 301 Redirect Guide Section Component
const RedirectGuideSection: React.FC<{
  isPremium: boolean;
  onAddTodo: (text: string, category: 'backlinks' | 'redirects') => void;
  onCopy: (text: string, id: string) => void;
  copiedId: string | null;
}> = ({ isPremium, onAddTodo, onCopy, copiedId }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['what-is']));

  const toggleSection = (id: string) => {
    const newSet = new Set(expandedSections);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedSections(newSet);
  };

  const checklistItems = [
    { id: 'niche', text: t('backlinks.checkNiche') || 'Check niche relevance (MUST PASS)', required: true },
    { id: 'da', text: t('backlinks.checkDA') || 'Domain Authority: DR/DA 20+ (PASS)', required: false },
    { id: 'spam', text: t('backlinks.checkSpam') || 'Spam Score: Under 30% (MUST PASS)', required: true },
    { id: 'wayback', text: t('backlinks.checkWayback') || 'Wayback Machine: Legit content, no spam (MUST PASS)', required: true },
    { id: 'index', text: t('backlinks.checkIndex') || 'Google Index: Still indexed (PASS)', required: false },
    { id: 'backlinks', text: t('backlinks.checkBacklinks') || 'Backlink Quality: Real sites, not spam (PASS)', required: false },
    { id: 'dmca', text: t('backlinks.checkDMCA') || 'DMCA Check: No copyright issues (MUST PASS)', required: true },
    { id: 'blacklist', text: t('backlinks.checkBlacklist') || 'Blacklist Check: Not blacklisted (MUST PASS)', required: true },
    { id: 'age', text: t('backlinks.checkAge') || 'Domain Age: 5+ years old (NICE TO HAVE)', required: false },
  ];

  const htaccessCode = `RewriteEngine On
RewriteCond %{HTTP_HOST} ^expireddomain\\.com$ [OR]
RewriteCond %{HTTP_HOST} ^www\\.expireddomain\\.com$
RewriteRule ^(.*)$ https://yourmainsite.com/$1 [R=301,L]`;

  const pageRedirectCode = `RewriteEngine On

# Redirect specific page
Redirect 301 /best-pizza-recipe https://yourmainsite.com/pizza-recipes

# Redirect another page
Redirect 301 /italian-food https://yourmainsite.com/italian-cooking

# Redirect everything else to homepage
RewriteCond %{REQUEST_URI} !^/best-pizza-recipe
RewriteCond %{REQUEST_URI} !^/italian-food
RewriteRule ^(.*)$ https://yourmainsite.com/ [R=301,L]`;

  return (
    <div className="space-y-8">
      {/* What is 301 Redirect */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <button
          onClick={() => toggleSection('what-is')}
          className="w-full flex items-center justify-between mb-4"
        >
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Shield className="text-indigo-400" size={24} />
            {t('backlinks.whatIs301') || '🔍 What is the 301 Redirect Method?'}
          </h3>
          <ArrowRight 
            className={`transition-transform ${expandedSections.has('what-is') ? 'rotate-90' : ''}`}
            size={20}
          />
        </button>
        {expandedSections.has('what-is') && (
          <div className="space-y-4 text-slate-300">
            <p>{t('backlinks.redirectExplanation') || 'Simple explanation: You buy an expired domain that already has good backlinks and high DA/DR, then you "redirect" it to your website so all that SEO power (link juice) transfers to your site.'}</p>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <h4 className="font-semibold text-white mb-2">{t('backlinks.howItWorks') || 'How it works:'}</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>{t('backlinks.step1') || 'Domain XYZ.com expires (previous owner didn\'t renew)'}</li>
                <li>{t('backlinks.step2') || 'XYZ.com has 500 backlinks, DR 40, DA 35'}</li>
                <li>{t('backlinks.step3') || 'You buy XYZ.com for $50-500'}</li>
                <li>{t('backlinks.step4') || 'You set up a 301 redirect from XYZ.com → YourWebsite.com'}</li>
                <li>{t('backlinks.step5') || 'All the backlinks and authority from XYZ.com now boost YOUR website'}</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Google Update Warning */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-bold text-red-400 mb-2">
              {t('backlinks.googleUpdate') || '⚠️ CRITICAL: Google\'s 2024 Update on Expired Domains'}
            </h3>
            <div className="space-y-3 text-slate-300 text-sm">
              <div>
                <p className="font-semibold text-white mb-1">{t('backlinks.whatsBanned') || 'What\'s BANNED:'}</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{t('backlinks.banned1') || 'Buying 50+ expired domains and redirecting them all'}</li>
                  <li>{t('backlinks.banned2') || 'Buying domains completely unrelated to your niche'}</li>
                  <li>{t('backlinks.banned3') || 'Automated expired domain networks'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-emerald-400 mb-1">{t('backlinks.whatsOk') || 'What\'s STILL OK:'}</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{t('backlinks.ok1') || 'Buying 1-3 relevant expired domains'}</li>
                  <li>{t('backlinks.ok2') || 'Redirecting domains in the SAME niche as yours'}</li>
                  <li>{t('backlinks.ok3') || '"Merger technique" (acquiring a similar business)'}</li>
                </ul>
              </div>
              <p className="text-white font-medium mt-3">
                {t('backlinks.bottomLine') || 'Bottom line: As long as the domain is RELEVANT to your niche and you\'re not doing it at massive scale, you\'re fine.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <FileText className="text-indigo-400" size={24} />
          {t('backlinks.checklist') || '🎯 Complete Checklist Before Buying'}
        </h3>
        <div className="space-y-3">
          {checklistItems.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors cursor-pointer"
              onClick={() => isPremium && onAddTodo(item.text, 'redirects')}
            >
              {item.required ? (
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              )}
              <span className={`text-sm flex-1 ${item.required ? 'text-white font-semibold' : 'text-slate-300'}`}>
                {item.text}
              </span>
              {isPremium && (
                <ListTodo className="w-4 h-4 text-slate-500" size={16} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* How to Do 301 Redirect */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Zap className="text-indigo-400" size={24} />
          {t('backlinks.howToRedirect') || '🛠️ How to Do 301 Redirect - Step by Step'}
        </h3>
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-white mb-2">{t('backlinks.optionA') || 'OPTION A: Redirect in Hostinger'}</h4>
            <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm ml-4">
              <li>{t('backlinks.hostinger1') || 'Buy the expired domain at Namecheap, GoDaddy, etc.'}</li>
              <li>{t('backlinks.hostinger2') || 'Point domain to Hostinger nameservers'}</li>
              <li>{t('backlinks.hostinger3') || 'Add domain to Hostinger hPanel'}</li>
              <li>{t('backlinks.hostinger4') || 'Set up 301 redirect using Hostinger\'s Redirect Tool or .htaccess file'}</li>
            </ol>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">{t('backlinks.optionB') || 'OPTION B: Redirect in Namecheap'}</h4>
            <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm ml-4">
              <li>{t('backlinks.namecheap1') || 'Buy/transfer expired domain to Namecheap'}</li>
              <li>{t('backlinks.namecheap2') || 'Go to Domain List → Manage → Redirect Domain'}</li>
              <li>{t('backlinks.namecheap3') || 'Choose "Permanent Redirect (301)"'}</li>
              <li>{t('backlinks.namecheap4') || 'Enter your target URL and save'}</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Code Examples */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Code className="text-indigo-400" size={24} />
          {t('backlinks.codeExamples') || '💡 Code Examples'}
        </h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-white">{t('backlinks.basicRedirect') || 'Basic Domain Redirect (.htaccess)'}</h4>
              {isPremium && (
                <button
                  onClick={() => onCopy(htaccessCode, 'htaccess')}
                  className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  {copiedId === 'htaccess' ? <Check size={16} /> : <Copy size={16} />}
                </button>
              )}
            </div>
            <pre className="bg-slate-900 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto">
              <code>{htaccessCode}</code>
            </pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-white">{t('backlinks.pageRedirect') || 'Page-to-Page Redirects (Best Method)'}</h4>
              {isPremium && (
                <button
                  onClick={() => onCopy(pageRedirectCode, 'pageredirect')}
                  className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  {copiedId === 'pageredirect' ? <Check size={16} /> : <Copy size={16} />}
                </button>
              )}
            </div>
            <pre className="bg-slate-900 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto">
              <code>{pageRedirectCode}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-2xl p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Clock className="text-indigo-400" size={24} />
          {t('backlinks.timeline') || '⏱️ Timeline & Expectations'}
        </h3>
        <div className="space-y-2 text-slate-300 text-sm">
          <p><strong className="text-white">Week 1-2:</strong> {t('backlinks.timeline1') || 'Google discovers the redirect'}</p>
          <p><strong className="text-white">Week 3-4:</strong> {t('backlinks.timeline2') || 'Backlinks start transferring authority'}</p>
          <p><strong className="text-white">Month 2-3:</strong> {t('backlinks.timeline3') || 'Full SEO impact visible'}</p>
          <p><strong className="text-white">Month 3-6:</strong> {t('backlinks.timeline4') || 'Rankings stabilize'}</p>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <XCircle className="text-red-400" size={24} />
          {t('backlinks.commonMistakes') || '🚨 Common Mistakes to Avoid'}
        </h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake1') || 'Buying irrelevant domains (biggest mistake)'}</span>
          </li>
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake2') || 'Redirecting spammy domains'}</span>
          </li>
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake3') || 'Buying too many domains at once (looks suspicious)'}</span>
          </li>
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake4') || 'Redirecting to homepage only (redirect to relevant pages)'}</span>
          </li>
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake5') || 'Not checking Wayback Machine'}</span>
          </li>
          <li className="flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span>{t('backlinks.mistake6') || 'Ignoring spam score'}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

